/**
 * Logged-out teaser rails — Phase 5: use get_discovery_feed teaser shape (free tier),
 * never direct table SELECT on events/venues.
 */
import { fetchDiscoveryFeed } from "@/lib/discoveryFeed";
import { resolveDiscoveryImage } from "@/lib/discoveryFeedMap";

export const WEB_TEASER_LIMIT = 5;

export type TeaserVenue = {
  id: string;
  category: string;
  photo_url: string | null;
  city: string | null;
};

export type TeaserEvent = {
  id: string;
  photo_url: string | null;
  starts_at: string | null;
  venueCategory: string | null;
};

const CITY_KEY = "zurich";

/** Logged-out live teaser — capped, no names/addresses/coords. */
export async function fetchTeaserVenuesForCity(_city: string): Promise<TeaserVenue[]> {
  const feed = await fetchDiscoveryFeed(CITY_KEY);
  const teasers = feed.venues.teasers.slice(0, WEB_TEASER_LIMIT);
  // Prefer teasers; if free cap puts all in full, use full rows stripped to teaser fields.
  const source =
    teasers.length > 0
      ? teasers
      : feed.venues.full.slice(0, WEB_TEASER_LIMIT).map((row) => ({
          id: String(row.id),
          category: String(row.category ?? "Bars"),
          teaser_image_url: row.photo_url != null ? String(row.photo_url) : null,
          is_top_pick: Boolean(row.is_top_pick),
        }));

  return source.map((row) => ({
    id: row.id,
    category: row.category,
    photo_url: resolveDiscoveryImage(row.teaser_image_url, row.category),
    city: null,
  }));
}

export async function fetchTeaserEventsForCity(_city: string): Promise<TeaserEvent[]> {
  const feed = await fetchDiscoveryFeed(CITY_KEY);
  const teasers = feed.live_events.teasers.slice(0, WEB_TEASER_LIMIT);
  const source =
    teasers.length > 0
      ? teasers
      : feed.live_events.full.slice(0, WEB_TEASER_LIMIT).map((row) => ({
          id: String(row.id),
          category: String(row.category ?? row.venue_category ?? "Events"),
          teaser_image_url:
            row.photo_url != null
              ? String(row.photo_url)
              : row.venue_photo_url != null
                ? String(row.venue_photo_url)
                : null,
          is_top_pick: Boolean(row.is_top_pick),
          starts_at: row.starts_at != null ? String(row.starts_at) : null,
        }));

  return source.map((row) => ({
    id: row.id,
    photo_url: resolveDiscoveryImage(
      "teaser_image_url" in row ? row.teaser_image_url : null,
      row.category,
    ),
    starts_at: "starts_at" in row && row.starts_at != null ? String(row.starts_at) : null,
    venueCategory: row.category,
  }));
}
